import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { SmartFilter } from '../../components/common/SmartFilter';
import { UserList } from '../../components/ui/list/UserList';
import Pagination from '../../components/atoms/Pagination';
import { headerUserTable } from '../../utils/constants';
import { GETUsers, BulkDeleteUsers } from '../../services/users';
import { IUser } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import CustomerFilter, {
  CustomerDateFilterData,
} from '../../components/ui/filters/customer';
import withComponentAuth from '../../hoc/withComponentAuth';
import CreateUserModal from '../Booking/actions/AddUserModal';
import { useUrlSearchQuery } from '../../hooks/useUrlSearchQuery';
import { useAdminPermission } from '../../hooks/useAdminPermission';
import { DialogBoxComp } from '../../components/atoms/DialogBox';
import useSnackbar from '../../hooks/useSnackbar';
import { APP_SHELL_HEADER_CONTROL_HEIGHT } from '../../components/layout/layoutConstants';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const UserManagement = () => {
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [users, setUsers] = useState<IUser[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterFlag, setFilterFlag] = useState<boolean>(false);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const { canDelete } = useAdminPermission();
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const hasDeletePermission = canDelete('customers');

  useUrlSearchQuery((query) => {
    setSearchQuery(query);
    setPage(1);
  });

  // Fetch users whenever page, rowsPerPage, or searchQuery changes
  useEffect(() => {
    !filterFlag && fetchUsers(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, searchQuery]);

  useEffect(() => {
    filterFlag &&
      fetchUsers(page, rowsPerPage, searchQuery, startDate, endDate);
  }, [startDate, endDate, page, rowsPerPage, searchQuery, sort]);

  // Fetch users from API
  const fetchUsers = async (
    page: number,
    limit: number,
    searchQuery?: string,
    SDate?: Date | null,
    EDate?: Date | null,
  ) => {
    try {
      setIsLoading(true);
      const response = await GETUsers({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
        startDate: SDate,
        endDate: EDate,
      });
      setUsers(response.data); // Set the user data from the API
      setTotalUsers(response.count); // Set the total number of users
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const refeshList = () => {
    fetchUsers(page, rowsPerPage, searchQuery);
  };

  const handleSaveRefresh = () => {
    refeshList();
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search); // Update the search query state
    setPage(1); // Reset to the first page for new search
  }, 500); // 500ms delay before search executes

  const handleSortRefresh = () => {
    setSort(sort == 'ASC' ? 'DESC' : 'ASC');
    refeshList();
  };

  const handleresetFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterFlag(false);
    refeshList();
  };

  const handleFilterRefresh = (Filter: CustomerDateFilterData) => {
    if (Filter.startDate && Filter.endDate) {
      setStartDate(new Date(Filter.startDate));
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    } else if (Filter.startDate) {
      setStartDate(new Date(Filter.startDate));
      setFilterFlag(true);
    } else if (Filter.endDate) {
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    }
  };

  const handleAddModal = () => {
    setOpenAddModal(true);
  };

  const handleSelectionChange = (ids: string[]) => {
    setSelectedIds(ids);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;

    try {
      setIsDeleting(true);
      const result = await BulkDeleteUsers(selectedIds);
      showSnackbar(`${result.deletedCount} user(s) deleted successfully`, 'success');
      setSelectedIds([]);
      setOpenDeleteModal(false);
      refeshList();
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to delete users', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}
      <SmartFilter
        title="Users"
        createFilter={['SEARCH', 'ADD', 'FILTER']}
        // addElement={<CreateUserComp />}
        filterElement={
          <CustomerFilter
            onResetFilter={handleresetFilter}
            startDate={
              startDate ? new Date(startDate).toISOString().split('T')[0] : null
            }
            endDate={
              endDate ? new Date(endDate).toISOString().split('T')[0] : null
            }
          />
        }
        addbuttontitle="Add User"
        filterHeader={'Filter Users'}
        addHeader={'Add Users'}
        onSaved={handleSaveRefresh}
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
        onSort={handleSortRefresh}
        onNavigate={handleAddModal}
        extraActions={
          hasDeletePermission && selectedIds.length > 0 ? (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={() => setOpenDeleteModal(true)}
              sx={{ textTransform: 'none', borderRadius: 2, height: APP_SHELL_HEADER_CONTROL_HEIGHT }}
            >
              Delete ({selectedIds.length})
            </Button>
          ) : null
        }
      />

      <CreateUserModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSaved={() => {
          handleSaveRefresh();
          setOpenAddModal(false);
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        {/* <CreateTableHeader tableCell={headerUserTable} /> */}
        <Box
          ref={scrollRef}
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {users.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Users Found
            </Typography>
          ) : (
            <UserList
              headerCells={headerUserTable}
              users={users}
              onUpdate={refeshList}
              isLoading={isLoading}
              showCheckbox={hasDeletePermission}
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}
            />
          )}
        </Box>

        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalUsers}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>

        <DialogBoxComp
          open={openDeleteModal}
          title="Delete Users"
          content={`Are you sure you want to delete ${selectedIds.length} user(s)? This action will disable the selected user accounts.`}
          handleClose={() => setOpenDeleteModal(false)}
          handleConfirm={handleDeleteSelected}
        />
      </Box>
    </Box>
  );
};

export default withComponentAuth(UserManagement);
